import Image from '@/components/ui/Image';
import { Link } from 'react-router-dom';
import { Server, Building2, MapPin, GraduationCap, Target, Eye, Heart, ExternalLink, Shield } from 'lucide-react';
import AnimatedSection from '@/components/ui/AnimatedSection';
import SectionTitle from '@/components/ui/SectionTitle';
import DotNavigation from '@/components/layout/DotNavigation';
import Footer from '@/components/sections/Footer';
import { useProjects } from '@/hooks/useProjects';
const features = [
    {
        icon: Server,
        number: '01',
        heading: '電気・配管工事の一括施工',
        text: '各種配管工事・電気設備工事・太陽光発電工事まで、複数業者に分離せずワンストップで請け負います。工程短縮と高品質を両立。',
    },
    {
        icon: Building2,
        number: '02',
        heading: '多様な施工対象',
        text: '商業施設・公共施設・工場・店舗・住宅など、規模や用途を問わず柔軟に対応。CADでの設計提案も可能です。',
    },
    {
        icon: Shield,
        number: '03',
        heading: '安全・品質・工程の徹底',
        text: '安全を最優先に、高い品質と適切な工程管理を徹底。元請企業様・お施主様双方のご満足を追求します。',
    },
    {
        icon: MapPin,
        number: '04',
        heading: '充実した対応体制',
        text: 'グリーンサイト・CCUS・ビルディー完全対応、電子書類・電子発注対応。全国出張・夜間・緊急・短納期案件にも対応します。',
    },
    {
        icon: Heart,
        number: '05',
        heading: '長く信頼される関係構築',
        text: '短期的な取引ではなく、長く信頼いただける関係構築を目指します。誠実なコミュニケーションと確かな施工でお客様の期待を超える仕事を。',
    },
    {
        icon: GraduationCap,
        number: '06',
        heading: '資格取得支援と人材育成',
        text: '第一種電気工事士・1級施工管理技士など多数在籍。資格取得費用は会社全額負担で、若手が成長できる環境を整えています。',
    },
];
const philosophyItems = [
    {
        icon: Target,
        title: 'MISSION',
        description: '社員とその家族の幸せを追求する',
    },
    {
        icon: Eye,
        title: 'VISION',
        description: '岩手から全国へ—\n次世代のライフラインを支える企業へ',
    },
    {
        icon: Heart,
        title: 'VALUES',
        description: 'ENJOY / CHALLENGE / RESPECT',
    },
];
export default function AboutPage() {
    // データベースからworksデータを取得
    const { projects } = useProjects();
    const featuredWorks = projects.filter(p => p.status === 'published').slice(0, 3);
    return (<main className='relative'>
      <DotNavigation sections={['hero', 'ceo-message', 'philosophy', 'features', 'works', 'access', 'contact']}/>

      {/* Hero Section */}
      <section id='hero' className='relative h-screen overflow-hidden'>
        <div className='absolute inset-0'>
          <Image src='/images/about/about-hero.webp' alt='配管を掲げる若手技術者' className='w-full h-full object-cover' priority/>
          <div className='absolute inset-0 bg-gradient-to-b from-navy/40 via-navy/30 to-navy/60'/>
        </div>

        <div className='relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-24'>
          <h1 className='text-7xl md:text-8xl text-white font-bebas tracking-wider'>
            ABOUT US
          </h1>
          <p className='mt-6 text-xl md:text-2xl text-white font-noto'>
            電気と設備で、街に「いい空気」を。
          </p>
          <p className='mt-4 max-w-2xl text-white/90 text-lg leading-relaxed'>
            盛電設備工業株式会社は、各種配管工事・電気設備工事・太陽光発電工事をワンストップで提供する技術者集団です。
            <br />-仕事は楽しく-
              <br />私たちは岩手・盛岡から全国へ、確かな技術とさわやかな笑顔をお届けします。
          </p>
        </div>
      </section>

      {/* CEO Message Section */}
      <AnimatedSection id='ceo-message' className='bg-white'>
        <div className='section-container'>
          <div className='max-w-3xl mx-auto'>
            <div className='mb-8'>
              <p className='text-skyblue font-medium mb-2'>代表取締役 舘澤 祐太</p>
              <h2 className='text-5xl md:text-6xl font-bebas tracking-wider text-navy'>
                MESSAGE FROM CEO
              </h2>
            </div>

            <div className='space-y-5 text-navy/80 leading-relaxed text-lg'>
              <p>
                当社は管工事・電気工事を中心に、各種設備工事に携わってまいりました。
              </p>
              <p>
                創業以来、お客様との信頼関係を第一に考え、安全・品質・工程管理を徹底しながら、一つひとつの仕事に責任をもって取り組んでおります。
              </p>
              <p>
                建設業においては、単に施工を行うだけではなく『安心して任せられる対応力』が重要であると考えております。
              </p>
              <p>
                そのため当社では、迅速な対応、円滑なコミュニケーション、現場状況に応じた柔軟な提案力を大切にし、元請企業様・お施主様双方にご満足いただける施工体制づくりに努めております。
              </p>
              <p>
                また協力会社とのネットワークを活かし、多様な現場ニーズに対応できる体制を整えております。
              </p>
              <p>
                短期的な取引ではなく、長く信頼いただける関係構築を目指し、誠実な施工を積み重ねてまいります。
              </p>
              <p>
                今後とも、より一層の技術向上と安全管理の徹底に努め、皆様の期待に応えられる企業であり続けたいと考えております。
              </p>
              <p>
                引き続きご支援、ご愛顧のほど、よろしくお願い申し上げます。
              </p>
            </div>

            <div className='mt-8 p-6 bg-lightgray rounded-lg'>
              <p className='text-navy/70 text-sm'>
                代表取締役 舘澤 祐太
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Philosophy Section */}
      <AnimatedSection id='philosophy' className='bg-gray-50'>
        <div className='section-container'>
          <SectionTitle title='OUR PHILOSOPHY' subtitle='企業理念' description='私たちの使命、ビジョン、価値観をご紹介します。'/>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mt-16'>
            {philosophyItems.map((item, _index) => (<div key={item.title} className='bg-white p-8 rounded-lg text-center hover:shadow-lg transition-shadow'>
                <div className='w-16 h-16 rounded-full bg-skyblue/10 flex items-center justify-center mx-auto mb-6'>
                  <item.icon className='w-8 h-8 text-skyblue'/>
                </div>
                <h3 className='text-2xl font-bebas text-navy tracking-wide mb-4'>
                  {item.title}
                </h3>
                <p className='text-navy/80 leading-relaxed'>
                  {item.description}
                </p>
              </div>))}
          </div>
        </div>
      </AnimatedSection>

      {/* Features Section */}
      <AnimatedSection id='features' className='bg-white'>
        <div className='section-container'>
          <SectionTitle title='FEATURE' subtitle='盛電設備工業の特長' outlined/>

          <div className='mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12'>
            {features.map((feature) => (<div key={feature.number} className='flex items-start gap-8'>
                <div className='flex flex-col items-center'>
                  <div className='w-16 h-16 rounded-full border border-navy/20 flex items-center justify-center'>
                    <feature.icon size={24} className='text-skyblue'/>
                  </div>
                  <span className='mt-2 font-bebas text-2xl text-skyblue'>
                    {feature.number}
                  </span>
                </div>

                <div className='flex-1'>
                  <h3 className='text-xl font-noto font-bold text-navy'>
                    {feature.heading}
                  </h3>
                  <p className='mt-4 text-navy/80 leading-relaxed'>
                    {feature.text}
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </AnimatedSection>

      {/* Works Section */}
      <AnimatedSection id='works' className='bg-gray-50'>
        <div className='section-container'>
          <SectionTitle title='WORKS' subtitle='施工実例'/>

          <div className='mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {featuredWorks.map((work, _index) => (<Link key={work.id} to={`/works/${work.slug}`} className='group relative overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-lg transition-shadow'>
                <div className='relative aspect-[4/3]'>
                  <Image src={work.image_url} alt={work.title} className='object-cover transition-transform duration-500 group-hover:scale-105'/>
                </div>

                <div className='p-8'>
                  <div className='flex items-center gap-3 mb-4'>
                    <span className='px-3 py-1 bg-navy/10 text-navy text-sm rounded-full'>
                      {work.category}
                    </span>
                    <span className='text-sm text-navy/60'>{work.location}</span>
                  </div>
                  <h3 className='text-xl font-medium text-navy mb-4'>
                    {work.title}
                  </h3>
                  <p className='text-navy/70 text-sm leading-relaxed'>
                    {work.description}
                  </p>
                </div>
              </Link>))}
          </div>

          <div className='mt-12 text-center'>
            <Link to='/works' className='btn-outline'>
              すべての施工事例を見る
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* Access Map Section */}
      <AnimatedSection id='access' className='bg-white'>
        <div className='section-container'>
          <SectionTitle title='ACCESS' subtitle='アクセス' description='盛電設備工業へのアクセス方法をご案内します。' outlined/>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16'>
            <div>
              <div className='bg-lightgray p-8 rounded-lg'>
                <h3 className='text-2xl font-bebas text-navy mb-6'>
                  COMPANY INFO
                </h3>
                
                <div className='space-y-4'>
                  <div>
                    <h4 className='font-medium text-navy mb-2'>所在地</h4>
                    <p className='text-navy/80'>
                      〒020-0839<br />
                      岩手県盛岡市津志田南3丁目13-15
                    </p>
                  </div>

                  <div>
                    <h4 className='font-medium text-navy mb-2'>アクセス</h4>
                    <ul className='text-navy/80 space-y-1'>
                      <li>• JR岩手飯岡駅 徒歩13分</li>
                      <li>• 盛岡南IC 車10分</li>
                      <li>• 敷地内に無料駐車場10台分あり</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className='font-medium text-navy mb-2'>営業時間</h4>
                    <p className='text-navy/80'>
                      平日 8:00〜18:00<br />
                      土日祝日 休業
                    </p>
                  </div>
                </div>

                <div className='mt-8'>
                  <a href='https://www.google.com/maps/place/%E7%9B%9B%E9%9B%BB%E8%A8%AD%E5%82%99%E5%B7%A5%E6%A5%AD%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BE/@39.6569846,141.1562302,17z/data=!3m1!4b1!4m6!3m5!1s0x5f859da8b3a2384b:0x75a0c808d06707c8!8m2!3d39.6569846!4d141.1562302!16s%2Fg%2F11h0_8_8_8' target='_blank' rel='noopener noreferrer' className='inline-flex items-center px-6 py-3 bg-navy text-white rounded-full font-medium transition-all hover:bg-opacity-90'>
                    Google Map を開く
                    <ExternalLink className='ml-2 w-4 h-4'/>
                  </a>
                </div>
              </div>
            </div>

            <div>
              <div className='bg-lightgray rounded-lg overflow-hidden h-full min-h-[400px]'>
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1673982.8460532392!2d138.3562302199914!3d40.40696456235923!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5f859da8b3a2384b%3A0x75a0c808d06707c8!2z55ub6Zu76Kit5YKZ5bel5qWt5qCq5byP5Lya56S-!5e1!3m2!1sja!2sjp!4v1749622329584!5m2!1sja!2sjp" width="100%" height="100%" style={{ border: 0, minHeight: '400px' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="盛電設備工業株式会社の所在地"/>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Contact Banner */}
      <AnimatedSection id='contact' className='bg-navy'>
        <div className='section-container'>
          <div className='flex flex-col lg:flex-row items-center justify-between'>
            <div>
              <h2 className='text-6xl font-bebas text-white tracking-wider'>
                GET IN TOUCH.
              </h2>
              <p className='mt-6 text-white/80 text-lg max-w-xl leading-relaxed'>
                ご相談・お見積はお気軽にどうぞ。フォームまたはお電話でご連絡ください。
              </p>
            </div>

            <div className='mt-8 lg:mt-0'>
              <Link to='/contact' className='btn-secondary'>
                お問い合わせフォームへ
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </main>);
}
